import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Shield, CheckCircle, AlertCircle, Info, User } from 'lucide-react';
import { TeamModel } from '@/database/models/team';
import { useUserRole } from '@/contexts/UserRoleContext';

export const AdminSetup: React.FC = () => {
  const [isSetupRunning, setIsSetupRunning] = useState(false);
  const [setupResult, setSetupResult] = useState<{
    success: boolean;
    message: string;
    error?: string;
    details?: string;
  } | null>(null);
  const [adminExists, setAdminExists] = useState(false);

  const { currentRole, isSetupMode, hasPermission } = useUserRole();

  useEffect(() => {
    checkAdminExists();
  }, []);

  const checkAdminExists = async () => {
    try {
      const members = await TeamModel.getTeamMembers();
      const adminFound = members.some(m => m.role === 'administrator');
      setAdminExists(adminFound);
      
      if (adminFound) {
        const admin = members.find(m => m.role === 'administrator');
        setSetupResult({
          success: true,
          message: 'Administrator account already exists',
          details: `Admin: ${admin?.name} (${admin?.email})`
        });
      }
    } catch (error) {
      console.error('Error checking admin:', error);
    }
  };

  const handleSetupAdmin = async () => {
    setIsSetupRunning(true);
    setSetupResult(null);

    try {
      console.log('🚀 Starting admin setup...');
      
      const admin = await TeamModel.initializeDefaultAdmin();
      
      if (admin) {
        console.log('✅ Admin setup completed:', admin);
        setSetupResult({
          success: true,
          message: 'Administrator account created successfully!',
          details: `Admin account created for ${admin.name} (${admin.email}). You can now login with this email.`
        });
        setAdminExists(true);
      } else {
        setSetupResult({
          success: true,
          message: 'Administrator account already exists',
          details: 'An administrator account was already found in the system.'
        });
        setAdminExists(true);
      }
    } catch (error) {
      console.error('❌ Setup failed:', error);
      setSetupResult({
        success: false,
        message: 'Setup failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsSetupRunning(false);
    }
  };

  const handleManualSetup = () => {
    setSetupResult({
      success: true,
      message: 'Manual Setup Instructions',
      details: 'Follow the steps below to set up admin manually'
    });
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2">
          <Shield className="w-5 h-5" />
          Administrator Setup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-gray-600">
          <p>This will create an administrator account with:</p>
          <ul className="mt-2 space-y-1 list-disc list-inside">
            <li>Email: dvdhabib@gmail.com</li>
            <li>Name: David Habib</li>
            <li>Role: Administrator</li>
            <li>Full system access</li>
          </ul>
        </div>

        {/* Debug Info */}
        <div className="bg-gray-50 p-3 rounded-lg text-xs space-y-1">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4" />
            <span className="font-medium">Current Status:</span>
          </div>
          <div>Role: <span className="font-mono">{currentRole}</span></div>
          <div>Setup Mode: <span className="font-mono">{isSetupMode ? 'Active' : 'Inactive'}</span></div>
          <div>Admin Exists: <span className="font-mono">{adminExists ? 'Yes' : 'No'}</span></div>
          <div>Can Add Members: <span className="font-mono">{hasPermission('canCreateAny') ? 'Yes' : 'No'}</span></div>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleSetupAdmin}
            disabled={isSetupRunning}
            className="w-full"
            variant={adminExists ? "outline" : "default"}
          >
            {isSetupRunning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Setting up...
              </>
            ) : adminExists ? (
              <>
                <User className="mr-2 h-4 w-4" />
                Check Admin Status
              </>
            ) : (
              <>
                <Shield className="mr-2 h-4 w-4" />
                Setup Administrator Account
              </>
            )}
          </Button>

          <Button
            onClick={handleManualSetup}
            variant="outline"
            className="w-full"
          >
            Show Manual Setup Instructions
          </Button>
        </div>

        {setupResult && (
          <Alert className={setupResult.success ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
            <div className="flex items-center gap-2">
              {setupResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <span className={`font-medium ${setupResult.success ? "text-green-800" : "text-red-800"}`}>
                {setupResult.message}
              </span>
            </div>
            <AlertDescription className={`mt-2 text-sm ${setupResult.success ? "text-green-700" : "text-red-700"}`}>
              {setupResult.details || setupResult.error}
              
              {setupResult.message === 'Manual Setup Instructions' && (
                <div className="mt-3 space-y-2">
                  <p className="font-medium">Manual Setup Steps:</p>
                  <ol className="list-decimal list-inside space-y-1 text-xs">
                    <li>Enable Setup Mode (if not already active)</li>
                    <li>Go to Teams menu</li>
                    <li>Click "Add Member" button</li>
                    <li>Fill in admin details:
                      <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5">
                        <li>Name: David Habib</li>
                        <li>Email: dvdhabib@gmail.com</li>
                        <li>Position: System Administrator</li>
                        <li>Role: Administrator</li>
                        <li>Department: IT</li>
                      </ul>
                    </li>
                    <li>Click "Add Member" to create the account</li>
                    <li>Exit Setup Mode</li>
                    <li>Login with the admin email</li>
                  </ol>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}; 